import { ChangeEvent, FC, useEffect, useState } from 'react';

import { Alert, FormLabel, Radio, RadioGroup } from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import { Info } from '@mui/icons-material';

import { getBrands } from 'api/queries/languages';
import DefaultSettingsImage from '../../assets/images/default-settings-image.jpg';
import Stroke1 from '../../assets/images/strokes_1@2x.png';
import { addFirestoreDocs, auth } from 'utils/config/firebase';
import Stroke3 from '../../assets/images/stroke_3@2x.png';
import Stroke4 from '../../assets/images/stroke_4@2x.png';
import Stroke2 from '../../assets/images/strokes_2.png';
import PrimaryButton from 'components/Common/PrimaryButton';

import {
    BRANDS,
    OUTPUT_LANGUAGES,
    TARGET_COUNTRIES,
    USER_SETTINGS,
} from 'utils/config/constants/controllerNames';
import { BRAND, BrandType, CLIENT } from './types';
import { useTranslation } from 'react-i18next';
import { errorLogger } from 'utils/config/firebase/logs';
import { useAppSelector } from 'app/hooks';
import { useDispatch } from 'react-redux';
import { setOutputLanguages, setPrivacyPolicyModalOpen } from 'store/global/globalSlice';
import { getTranslatedDataLanguages, getTranslatedTargetCountries } from 'helpers/filters';

const DefaultSettings: FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { outputLanguages, targetCountries } = useAppSelector((state) => state.global);
    const [selectedOutputLanguage, setSelectedOutputLanguage] = useState<string>('');
    const [selectedTargetCountry, setSelectedTargetCountry] = useState<string>('');

    const [selectedBrand, setSelectedBrand] = useState<string>('');
    const [brands, setBrands] = useState<BrandType[] | []>([]);
    const [countryBrands, setCountryBrands] = useState<BrandType[] | []>([]);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

    const [userType, setUserType] = useState<string>(BRAND);

    useEffect(() => {
        getBrands(setBrands);
        dispatch(setPrivacyPolicyModalOpen(true));
        // getTaretCountries(setTargetCountries);
    }, [dispatch]);

    const handleTargetSelect = (value: string) => {
        const newBrands = brands.filter((item) => item.countries?.includes(value));

        setCountryBrands(newBrands);
        if (!newBrands.find((item) => item.key === selectedBrand)) {
            setSelectedBrand('');
        }

        dispatch(
            setOutputLanguages(
                outputLanguages.map((item) =>
                    item.country_code === value
                        ? { ...item, shown: true }
                        : { ...item, shown: false },
                ),
            ),
        );
        setSelectedOutputLanguage('');
        setSelectedTargetCountry(value);
    };

    const handleSelect = (event: SelectChangeEvent, type: string) => {
        const value = event.target.value;
        switch (type) {
            case BRANDS:
                setSelectedBrand(value);
                break;
            case OUTPUT_LANGUAGES:
                setSelectedOutputLanguage(value);
                break;
            case TARGET_COUNTRIES:
                handleTargetSelect(value);
                break;
            default:
                break;
        }
    };

    const handleCheckBox = (e: ChangeEvent<HTMLInputElement>) => {
        setUserType(e.target.value);
        setSelectedBrand('');
    };

    const isAllSelected = !!(
        selectedOutputLanguage &&
        selectedTargetCountry &&
        (selectedBrand || userType === CLIENT)
    );

    const handleSubmit = () => {
        try {
            setIsSubmitted(true);
            const data = {
                user_type: userType,
                brand: selectedBrand,
                target_country: targetCountries?.find(
                    (item) => item.country_code === selectedTargetCountry,
                )?.title,
                target_country_code: selectedTargetCountry,
                output_language_title: selectedOutputLanguage,
                output_language_code: outputLanguages.find(
                    (item) => item.name === selectedOutputLanguage,
                )?.language_code,
                user_id: auth?.currentUser?.uid,
                show_full_product_tour: true,
                dont_show_general_generate: false,
                dont_show_additional_settings_example: false,
            };
            addFirestoreDocs(USER_SETTINGS, data);
        } catch (error) {
            if (error instanceof Error) {
                errorLogger.mediumError(error?.message, 'handleSubmit');
            }
            console.log(error);
        }
    };

    return (
        <>
            <Box className="default-screen-container">
                <Box>
                    <img className="default-settings-img" src={DefaultSettingsImage} />
                    <img className="stroke stroke-1" src={Stroke1} />
                    <img className="stroke stroke-2" src={Stroke2} />
                    <img className="stroke stroke-3" src={Stroke3} />
                    <img className="stroke stroke-4" src={Stroke4} />
                </Box>
                <Box className="default-screen flex">
                    <Typography variant="h1" className="mb-80">
                        {t('web_app.default_settings.title')}
                    </Typography>
                    <Typography variant="h5" className="mb-30">
                        {t('web_app.default_settings.global_label')}
                    </Typography>
                    <Box className="basics-form mb-30">
                        <Box className="basics-form-bottom mb-16 flex">
                            <Box>
                                <FormLabel>
                                    {t('web_app.default_settings.target_country_title')} *
                                </FormLabel>
                                <FormControl variant="standard">
                                    <InputLabel id="country-label">
                                        {t('web_app.default_settings.target_country_title')}
                                    </InputLabel>
                                    <Select
                                        labelId="country-label"
                                        id="country-select"
                                        value={selectedTargetCountry}
                                        onChange={(e) => handleSelect(e, TARGET_COUNTRIES)}
                                        label="Country"
                                    >
                                        {getTranslatedTargetCountries(targetCountries).map(
                                            (item) => (
                                                <MenuItem
                                                    key={item.document_id}
                                                    value={item.country_code}
                                                >
                                                    {item.title}
                                                </MenuItem>
                                            ),
                                        )}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box>
                                <FormLabel>
                                    {t('web_app.default_settings.target_language')} *
                                </FormLabel>
                                <FormControl variant="standard">
                                    <InputLabel id="job-language-label">
                                        {t('web_app.default_settings.target_language_title')}
                                    </InputLabel>
                                    <Select
                                        labelId="job-language-label"
                                        id="job-language-select"
                                        value={selectedOutputLanguage}
                                        onChange={(e) => handleSelect(e, OUTPUT_LANGUAGES)}
                                        disabled={!selectedTargetCountry}
                                        label={t('web_app.default_settings.target_language')}
                                    >
                                        {getTranslatedDataLanguages(outputLanguages)
                                            .filter((item) => item.shown)
                                            .map((item) => (
                                                <MenuItem key={item.id} value={item.name}>
                                                    {item.language_name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                        <Box
                            className={`basics-form-top mb-12 flex ${
                                !selectedTargetCountry ? 'disabled-view' : ''
                            }`}
                        >
                            <FormControl>
                                <RadioGroup
                                    value={userType}
                                    defaultValue={BRAND}
                                    onChange={handleCheckBox}
                                >
                                    <FormControlLabel
                                        value={CLIENT}
                                        control={<Radio />}
                                        label={
                                            <div>
                                                {t('web_app.default_settings.represent_client')}
                                                <div className="label-description">
                                                    {t(
                                                        'web_app.default_settings.represent_client_description',
                                                    )}
                                                </div>
                                            </div>
                                        }
                                        className="mb-16"
                                        disabled={!selectedTargetCountry}
                                    />
                                    <FormControlLabel
                                        value={BRAND}
                                        control={<Radio />}
                                        label={
                                            <div>
                                                {t('web_app.default_settings.represent_brand')}
                                                <div className="label-description">
                                                    {t(
                                                        'web_app.default_settings.represent_brand_description',
                                                    )}
                                                </div>
                                            </div>
                                        }
                                        disabled={!selectedTargetCountry}
                                    />
                                </RadioGroup>
                            </FormControl>
                            <FormControl variant="standard">
                                <InputLabel id="brand-label">
                                    {t('web_app.default_settings.represent_brand_title')}
                                </InputLabel>
                                <Select
                                    labelId="brand-label"
                                    id="brand-select"
                                    value={selectedBrand}
                                    onChange={(e) => handleSelect(e, BRANDS)}
                                    label={t('web_app.default_settings.represent_brand_title')}
                                    disabled={userType !== BRAND || !selectedTargetCountry}
                                >
                                    {countryBrands.map((item) => (
                                        <MenuItem key={item.document_id} value={item.key}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Alert
                            icon={<Info />}
                            className="default-screen-alert"
                            variant="filled"
                            severity="info"
                        >
                            {t('web_app.default_settings.information')}
                        </Alert>
                    </Box>
                    <PrimaryButton
                        className="done-button"
                        disabled={!isAllSelected || isSubmitted}
                        onClick={handleSubmit}
                        text={t('web_app.done')}
                    ></PrimaryButton>
                </Box>
            </Box>
        </>
    );
};

export default DefaultSettings;
